import { useCallback, useEffect, useState } from "react";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import Spinnerr from "../Commaon/Spinner";
import Toaster from "../Commaon/Toaster";
import { CgPen, CgTrash } from "react-icons/cg";
import { Modal } from "react-bootstrap";
import DeleteConfirmModal from "../Commaon/DeleteConfirmModal";
import ListHeader from "../Commaon/ListHeader";
import ListPagination from "../Commaon/ListPagination";

const DAYS = [
  { code: "MON", label: "Mon" },
  { code: "TUE", label: "Tue" },
  { code: "WED", label: "Wed" },
  { code: "THU", label: "Thu" },
  { code: "FRI", label: "Fri" },
  { code: "SAT", label: "Sat" },
  { code: "SUN", label: "Sun" },
];
const ALL_DAY_CODES = DAYS.map((d) => d.code);
const MON_TO_SAT = ALL_DAY_CODES.filter((code) => code !== "SUN");
const MAX_START_MINUTES = 23 * 60;

// Today's weekday code (MON..SUN), used as the initially selected day
const todayCode = () => ALL_DAY_CODES[(new Date().getDay() + 6) % 7];

const parseMinutes = (hhmm) => {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(String(hhmm || "").trim());
  return match ? Number(match[1]) * 60 + Number(match[2]) : null;
};

const toHHmm = (minutes) => {
  const m = ((minutes % 1440) + 1440) % 1440;
  return `${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

const to12h = (hhmm) => {
  const minutes = parseMinutes(hhmm);
  if (minutes === null) return hhmm || "-";
  const h = Math.floor(minutes / 60);
  const suffix = h >= 12 ? "PM" : "AM";
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(minutes % 60).padStart(2, "0")} ${suffix}`;
};

// End = start + 1 hour (a 23:00 slot ends at 00:00 / midnight)
const computeEnd = (start) => {
  const minutes = parseMinutes(start);
  return minutes === null ? "" : toHHmm(minutes + 60);
};

const formatRange = (slot) =>
  `${to12h(slot.startTime)} – ${to12h(slot.endTime || computeEnd(slot.startTime))}`;

const validateStart = (start) => {
  const minutes = parseMinutes(start);
  if (minutes === null) return "Please enter a valid start time";
  if (minutes > MAX_START_MINUTES) return "Start time must be between 12:00 AM and 11:00 PM";
  return null;
};

const sortByStart = (list) =>
  [...list].sort(
    (a, b) => (parseMinutes(a.startTime) ?? 0) - (parseMinutes(b.startTime) ?? 0)
  );

const WeeklySlots = () => {
  const [day, setDay] = useState(todayCode);
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);

  // search + pagination
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(25);

  // add modal
  const [addModel, setAddModel] = useState(false);
  const [addStart, setAddStart] = useState("");
  const [addDays, setAddDays] = useState([]);
  const [addTouched, setAddTouched] = useState(false);
  const [addSkipped, setAddSkipped] = useState([]);
  const [addError, setAddError] = useState("");

  // edit modal
  const [editModel, setEditModel] = useState(false);
  const [editSlot, setEditSlot] = useState(null);
  const [editStart, setEditStart] = useState("");
  const [editError, setEditError] = useState("");

  // delete modals
  const [deleteId, setDeleteId] = useState(null);
  const [bulkDeleteModel, setBulkDeleteModel] = useState(false);

  const notify = (message, type = "success") => setSnack({ notification: { message, type } });

  const getSlots = useCallback(async (dayCode) => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(`/admin/slots?day=${dayCode}`, { method: "GET" });
      const data = response.data || {};
      if (data.s === 200) {
        setSlots(sortByStart(Array.isArray(data.r) ? data.r : []));
      } else {
        setSlots([]);
        if (data.m) setSnack({ notification: { message: data.m, type: "error" } });
      }
    } catch (err) {
      console.error("Failed to fetch slots:", err);
      setSlots([]);
      setSnack({ notification: { message: "Error fetching slots", type: "error" } });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    setSelectedIds([]);
    setPageNo(1);
    getSlots(day);
  }, [day, getSlots]);

  useEffect(() => {
    setPageNo(1);
  }, [query]);

  const q = query.trim().toLowerCase();
  const filteredSlots = q
    ? slots.filter((slot) =>
        [slot.startTime, slot.endTime, formatRange(slot)].some((field) =>
          String(field ?? "").toLowerCase().includes(q)
        )
      )
    : slots;

  const startIndex = (pageNo - 1) * limit;
  const paginatedSlots = filteredSlots.slice(startIndex, startIndex + limit);
  const totalPages = Math.ceil(filteredSlots.length / limit) || 1;

  const visibleIds = paginatedSlots.map((slot) => slot._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  // ---- status ----
  const setStatus = (id, isActive) =>
    AuthInterceptor(`/admin/slots/${id}/status`, {
      method: "PATCH",
      body: JSON.stringify({ isActive }),
    });

  const toggleSlotStatus = async (slot) => {
    try {
      setLoading(true);
      const response = await setStatus(slot._id, !slot.isActive);
      const data = response.data || {};
      if (data.s === 200) {
        notify("Slot status updated");
        getSlots(day);
      } else {
        notify(data.m || "Error updating slot status", "error");
      }
    } catch (err) {
      console.error("Error updating slot status:", err);
      notify("Error updating slot status", "error");
    } finally {
      setLoading(false);
    }
  };

  const runBulkStatus = async (isActive) => {
    if (!selectedIds.length) {
      notify("Select at least one slot", "error");
      return;
    }
    // skip slots that are already in the target state
    const ids = selectedIds.filter(
      (id) => slots.find((item) => item._id === id)?.isActive !== isActive
    );
    if (!ids.length) {
      notify(`Selected slots are already ${isActive ? "active" : "inactive"}`, "alert");
      setSelectedIds([]);
      return;
    }
    try {
      setLoading(true);
      const results = await Promise.allSettled(ids.map((id) => setStatus(id, isActive)));
      const failed = results.filter(
        (r) => r.status !== "fulfilled" || r.value?.data?.s !== 200
      ).length;
      notify(
        failed ? `Updated ${ids.length - failed} of ${ids.length} slots` : "Bulk action completed",
        failed ? "error" : "success"
      );
      setSelectedIds([]);
      getSlots(day);
    } finally {
      setLoading(false);
    }
  };

  const openBulkDelete = () => {
    if (!selectedIds.length) {
      notify("Select at least one slot", "error");
      return;
    }
    setBulkDeleteModel(true);
  };

  // ---- delete ----
  const handleDelete = async () => {
    const id = deleteId;
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/slots/${id}`, { method: "DELETE" });
      const data = response.data || {};
      if (data.s === 200) {
        setSelectedIds((prev) => prev.filter((selectedId) => selectedId !== id));
        notify("Slot deleted successfully");
        getSlots(day);
      } else {
        notify(data.m || "Error deleting slot", "error");
      }
    } catch (err) {
      console.error("Error deleting slot:", err);
      notify("Error deleting slot", "error");
    } finally {
      setLoading(false);
      setDeleteId(null);
    }
  };

  const handleBulkDelete = async () => {
    const ids = [...selectedIds];
    try {
      setLoading(true);
      const response = await AuthInterceptor(`/admin/slots`, {
        method: "DELETE",
        body: JSON.stringify({ ids }),
      });
      const data = response.data || {};
      if (data.s === 200) {
        const deleted = data.r?.deleted ?? ids.length;
        notify(`${deleted} ${deleted === 1 ? "slot" : "slots"} deleted successfully`);
        setSelectedIds([]);
        getSlots(day);
      } else {
        notify(data.m || "Error deleting slots", "error");
      }
    } catch (err) {
      console.error("Error deleting slots:", err);
      notify("Error deleting slots", "error");
    } finally {
      setLoading(false);
      setBulkDeleteModel(false);
    }
  };

  // ---- add ----
  const openAddModal = () => {
    setAddStart("");
    setAddDays([day]);
    setAddTouched(false);
    setAddSkipped([]);
    setAddError("");
    setAddModel(true);
  };

  const closeAddModal = () => {
    setAddModel(false);
    setAddSkipped([]);
    setAddError("");
  };

  const toggleAddDay = (code) => {
    setAddDays((prev) =>
      prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]
    );
  };

  const sameDays = (a, b) => a.length === b.length && b.every((code) => a.includes(code));
  const togglePreset = (preset) => setAddDays((prev) => (sameDays(prev, preset) ? [] : [...preset]));

  const addStartError = validateStart(addStart);
  const addDaysError = addDays.length ? null : "Select at least one day";

  const handleAddSlot = async (e) => {
    e.preventDefault();
    setAddTouched(true);
    if (addStartError || addDaysError) return;

    try {
      setLoading(true);
      setAddError("");
      setAddSkipped([]);
      const orderedDays = ALL_DAY_CODES.filter((code) => addDays.includes(code));
      const response = await AuthInterceptor(`/admin/slots`, {
        method: "POST",
        body: JSON.stringify({ days: orderedDays, startTime: addStart }),
      });
      const data = response.data || {};
      const created = Array.isArray(data.r?.created) ? data.r.created : [];
      const skipped = Array.isArray(data.r?.skipped) ? data.r.skipped : [];

      if (data.s === 201 || data.s === 200) {
        const createdMsg = `${created.length} ${created.length === 1 ? "slot" : "slots"} added`;
        if (skipped.length) {
          notify(
            `${createdMsg}, ${skipped.length} skipped: ${skipped
              .map((s) => s.reason || s.day)
              .join("; ")}`,
            "alert"
          );
        } else {
          notify(data.m || createdMsg);
        }
        closeAddModal();
        getSlots(day);
      } else {
        // nothing created: keep the modal open and show why
        setAddSkipped(skipped);
        setAddError(data.m || "Failed to add slot");
      }
    } catch (err) {
      console.error("Error adding slot:", err);
      setAddError("Error adding slot");
    } finally {
      setLoading(false);
    }
  };

  // ---- edit ----
  const openEditModal = (slot) => {
    setEditSlot(slot);
    setEditStart(slot.startTime || "");
    setEditError("");
    setEditModel(true);
  };

  const closeEditModal = () => {
    setEditModel(false);
    setEditSlot(null);
    setEditError("");
  };

  const editStartError = validateStart(editStart);

  const handleEditSlot = async (e) => {
    e.preventDefault();
    if (editStartError) return;
    if (editSlot && editStart === editSlot.startTime) {
      notify("Slot is not changed", "alert");
      return;
    }
    try {
      setLoading(true);
      setEditError("");
      const response = await AuthInterceptor(`/admin/slots/${editSlot._id}`, {
        method: "PUT",
        body: JSON.stringify({ startTime: editStart }),
      });
      const data = response.data || {};
      if (data.s === 200) {
        notify("Slot updated successfully");
        closeEditModal();
        getSlots(day);
      } else {
        setEditError(data.m || "Failed to update slot");
      }
    } catch (err) {
      console.error("Error updating slot:", err);
      setEditError("Error updating slot");
    } finally {
      setLoading(false);
    }
  };

  const dayLabel = (code) => DAYS.find((d) => d.code === code)?.label || code;

  const renderEndPreview = (start) => {
    const end = validateStart(start) ? "" : computeEnd(start);
    return (
      <div className="mb-3">
        <label className="form-label">End Time</label>
        <input
          type="text"
          className="form-control"
          value={end ? `${to12h(end)}${end === "00:00" ? " (midnight)" : ""}` : "-"}
          readOnly
          disabled
        />
        <div className="form-text">Every slot is exactly 1 hour long.</div>
      </div>
    );
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && <Toaster notification={snack.notification} onClose={() => setSnack(null)} />}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <ListHeader
          title="Weekly Slots"
          searchValue={query}
          searchPlaceholder="Search by time..."
          onSearchChange={setQuery}
          limit={limit}
          onLimitChange={(size) => {
            setPageNo(1);
            setLimit(size);
          }}
        >
          <div className="btn-group btn-group-sm" role="group" aria-label="Select day">
            {DAYS.map((d) => (
              <button
                key={d.code}
                type="button"
                className="btn btn-outline-custom"
                style={d.code === day ? { backgroundColor: "#666cff", color: "#fff" } : undefined}
                onClick={() => d.code !== day && setDay(d.code)}
                aria-pressed={d.code === day}
              >
                {d.label}
              </button>
            ))}
          </div>
        </ListHeader>

        {/* Bulk actions | Add */}
        <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
          {paginatedSlots.length > 0 && (
            <>
              <span className="text-muted small">{selectedIds.length} selected</span>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkStatus(true)}>
                Active
              </button>
              <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkStatus(false)}>
                Inactive
              </button>
              <button className="btn btn-sm btn-outline-danger" onClick={openBulkDelete}>
                Delete
              </button>
            </>
          )}
          <button className="btn btn-sm btn-outline-custom ms-auto" onClick={openAddModal}>
            Add Slot
          </button>
        </div>

        <div className="table-responsive">
          <table className="table table-hover gap-2 align-middle">
            <thead className="table-light">
              <tr>
                <th style={{ width: "48px" }}>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    checked={allVisibleSelected}
                    onChange={toggleVisibleSelection}
                    aria-label="Select all slots on this page"
                  />
                </th>
                <th style={{ width: "60px" }}>Sr.</th>
                <th>Time</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading &&
                paginatedSlots.map((slot, idx) => (
                  <tr key={slot._id || idx}>
                    <td>
                      <input
                        type="checkbox"
                        className="form-check-input"
                        checked={selectedIds.includes(slot._id)}
                        onChange={() => toggleRowSelection(slot._id)}
                        aria-label={`Select slot ${formatRange(slot)}`}
                      />
                    </td>
                    <td>{startIndex + idx + 1}</td>
                    <td className="text-nowrap">{formatRange(slot)}</td>
                    <td>
                      <span
                        className={`badge ${slot.isActive ? "bg-success" : "bg-secondary"}`}
                        role="button"
                        onClick={() => toggleSlotStatus(slot)}
                      >
                        {slot.isActive ? "Active" : "Inactive"}
                      </span>
                    </td>
                    <td className="text-nowrap">
                      <button
                        className="btn btn-sm btn-outline-custom me-2"
                        onClick={() => openEditModal(slot)}
                      >
                        <CgPen size={16} />
                      </button>
                      <button
                        className="btn btn-sm btn-outline-danger"
                        onClick={() => setDeleteId(slot._id)}
                      >
                        <CgTrash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}

              {!loading && paginatedSlots.length === 0 && (
                <tr>
                  <td colSpan="5" className="text-center text-muted py-4">
                    {q ? "No slots match your search" : `No slots for ${dayLabel(day)}`}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <ListPagination
          show={filteredSlots.length > 0}
          pageNo={pageNo}
          totalPages={totalPages}
          onPageChange={setPageNo}
        />
      </div>

      {/* Add Slot Modal */}
      <Modal show={addModel} onHide={closeAddModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Add Slot</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleAddSlot} noValidate>
            <div className="row">
              <div className="col-12 col-sm-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="slot-add-start">
                    Start Time
                  </label>
                  <input
                    id="slot-add-start"
                    type="time"
                    className={`form-control ${addTouched && addStartError ? "is-invalid" : ""}`}
                    value={addStart}
                    min="00:00"
                    max="23:00"
                    onChange={(e) => setAddStart(e.target.value)}
                    onBlur={() => setAddTouched(true)}
                    disabled={loading}
                  />
                  {addTouched && addStartError ? (
                    <div className="invalid-feedback">{addStartError}</div>
                  ) : (
                    <div className="form-text">Between 12:00 AM and 11:00 PM.</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-sm-6">{renderEndPreview(addStart)}</div>
            </div>

            <div className="mb-3">
              <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                <label className="form-label mb-0 me-auto">Days</label>
                <button
                  type="button"
                  className={`btn btn-sm ${sameDays(addDays, ALL_DAY_CODES) ? "btn-secondary" : "btn-outline-secondary"}`}
                  onClick={() => togglePreset(ALL_DAY_CODES)}
                  disabled={loading}
                >
                  All days
                </button>
                <button
                  type="button"
                  className={`btn btn-sm ${sameDays(addDays, MON_TO_SAT) ? "btn-secondary" : "btn-outline-secondary"}`}
                  onClick={() => togglePreset(MON_TO_SAT)}
                  disabled={loading}
                >
                  Mon–Sat
                </button>
              </div>
              <div className="d-flex flex-wrap gap-3">
                {DAYS.map((d) => (
                  <div className="form-check" key={d.code}>
                    <input
                      id={`slot-add-day-${d.code}`}
                      type="checkbox"
                      className="form-check-input"
                      checked={addDays.includes(d.code)}
                      onChange={() => toggleAddDay(d.code)}
                      disabled={loading}
                    />
                    <label className="form-check-label" htmlFor={`slot-add-day-${d.code}`}>
                      {d.label}
                    </label>
                  </div>
                ))}
              </div>
              {addTouched && addDaysError && (
                <div className="text-danger small mt-1">{addDaysError}</div>
              )}
            </div>

            {addError && (
              <div className="alert alert-danger py-2 small">
                <div className="fw-semibold">{addError}</div>
                {addSkipped.length > 0 && (
                  <ul className="mb-0 mt-1 ps-3">
                    {addSkipped.map((s, i) => (
                      <li key={`${s.day}-${i}`}>
                        {dayLabel(s.day)}: {s.reason}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading || (addTouched && (!!addStartError || !!addDaysError))}
            >
              {loading ? <Spinnerr show={loading} /> : "Add Slot"}
            </button>
          </form>
        </Modal.Body>
      </Modal>

      {/* Edit Slot Modal */}
      <Modal show={editModel} onHide={closeEditModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Edit Slot ({dayLabel(editSlot?.day || day)})</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={handleEditSlot} noValidate>
            <div className="row">
              <div className="col-12 col-sm-6">
                <div className="mb-3">
                  <label className="form-label" htmlFor="slot-edit-start">
                    Start Time
                  </label>
                  <input
                    id="slot-edit-start"
                    type="time"
                    className={`form-control ${editStartError ? "is-invalid" : ""}`}
                    value={editStart}
                    min="00:00"
                    max="23:00"
                    onChange={(e) => setEditStart(e.target.value)}
                    disabled={loading}
                  />
                  {editStartError ? (
                    <div className="invalid-feedback">{editStartError}</div>
                  ) : (
                    <div className="form-text">Between 12:00 AM and 11:00 PM.</div>
                  )}
                </div>
              </div>
              <div className="col-12 col-sm-6">{renderEndPreview(editStart)}</div>
            </div>
            <div className="form-text mb-3">The day of a slot cannot be changed.</div>

            {editError && <div className="alert alert-danger py-2 small">{editError}</div>}

            <button type="submit" className="btn btn-primary" disabled={loading || !!editStartError}>
              {loading ? <Spinnerr show={loading} /> : "Update Slot"}
            </button>
          </form>
        </Modal.Body>
      </Modal>

      <DeleteConfirmModal
        show={!!deleteId}
        count={1}
        singular="slot"
        plural="slots"
        loading={loading}
        onConfirm={handleDelete}
        onCancel={() => setDeleteId(null)}
      />
      <DeleteConfirmModal
        show={bulkDeleteModel}
        count={selectedIds.length}
        singular="slot"
        plural="slots"
        loading={loading}
        onConfirm={handleBulkDelete}
        onCancel={() => setBulkDeleteModel(false)}
      />
    </>
  );
};

export default WeeklySlots;
